import {
  type ChangeEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'

import type { VaultMediaItemDto } from '@api'
import { useUnit } from 'effector-react'

import { Button } from '@/components/ui/button.tsx'
import { Dialog } from '@/components/ui/dialog.tsx'
import { TextField } from '@/components/ui/text-field.tsx'
import type { DialogProps } from '@/dialogs/types'
import { updateMediaSettingsFx } from '@/models/vault/medias'
import { setSelectedMedia } from '@/models/vault/medias/selection.ts'

interface Props extends DialogProps {
  item: VaultMediaItemDto
}

export const MediaSettingsDialog = ({ isOpen, item, onClose }: Props) => {
  // const isScriptsLoading = useUnit(getScriptsFx.pending)
  // const scripts = useUnit($scripts)
  const isMediaSettingsLoading = useUnit(updateMediaSettingsFx.pending)
  const inputRef = useRef<HTMLInputElement>(null)

  const [label, setLabel] = useState(item.mediaAttributes?.label || '')
  const [price, setPrice] = useState(item.mediaAttributes?.price || 0)
  const [scriptId, setScriptId] = useState(item.mediaAttributes?.scriptId ?? 0)

  const handleSave = useCallback(async () => {
    const scriptIdNumber = Number(scriptId)

    await updateMediaSettingsFx({
      mediaId: item.id,
      label,
      price: price ? price : undefined,
      scriptId: !scriptIdNumber ? undefined : scriptIdNumber,
    })
    onClose()
  }, [item, label, onClose, price, scriptId])

  const handlePriceChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const newValue = +event.target.value

      setPrice(isNaN(newValue) ? 0 : newValue)
    },
    [],
  )

  useEffect(() => {
    setSelectedMedia(item)
  }, [item])

  return (
    <Dialog
      show={isOpen}
      onClose={onClose}
      initialFocus={inputRef}
    >
      <Dialog.Header>
        <Dialog.Title>Media settings</Dialog.Title>
      </Dialog.Header>

      <div className="mt-5">
        <div className="mb-3">
          <label>Media Label</label>
          <TextField
            ref={inputRef}
            value={label}
            onChange={(event) => setLabel(event.target.value)}
            className="mt-0.5"
          />
        </div>

        <div className="mb-3">
          <label>Price</label>
          <TextField
            value={price}
            onChange={handlePriceChange}
            prefix={<span className="text-secondary-300">$</span>}
            className="mt-0.5"
          />
        </div>

        {/* Removed for now <div className="mb-3">
          <label className="mb-0.5 block">Highlight script</label>
          <Select
            value={scriptId ? scriptId.toString() : undefined}
            onValueChange={setScriptId}
            disabled={isScriptsLoading}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-h-[40vh] w-[300px]">
              <SelectGroup>
                <SelectItem value="0">No script</SelectItem>
                {scripts?.map(({ name, id }) => (
                  <SelectItem key={id} value={id.toString()}>
                    {name}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>*/}
      </div>

      <Dialog.Footer className="grid grid-cols-2 gap-2">
        <Button
          disabled={isMediaSettingsLoading}
          variant="outline"
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button
          isLoading={isMediaSettingsLoading}
          onClick={handleSave}
        >
          Save
        </Button>
      </Dialog.Footer>
    </Dialog>
  )
}
